import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, IdCard, Briefcase, FileText, Mic, PenLine, ListChecks, DollarSign, Handshake, Compass, ShieldCheck, CreditCard, BellRing, BadgeCheck, Sparkles,
} from "lucide-react";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import AgeReattestationGate from "@/components/talent/AgeReattestationGate";

/** Auth-gated shell for every /professional/* page.
 * Redirects to /login if not signed in, or to /employer/dashboard if role mismatch. */
const TalentLayout = () => {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [unreadApps, setUnreadApps] = useState(0);

    useEffect(() => {
        if (loading) return;
        if (!user) { navigate("/login"); return; }
        if (user.role !== "professional") {
            navigate(user.role === "employer" ? "/employer/dashboard" : "/");
        }
    }, [user, loading, navigate]);

    // Poll the tiny notifications endpoint so the red-dot updates when
    // employers advance the applicant's stage. Refreshes on every route
    // change so it clears the moment they hit /professional/applications.
    useEffect(() => {
        if (!user || user.role !== "professional") return;
        let cancelled = false;
        const refresh = () => {
            api.get("/professional/notifications/counts")
                .then(({ data }) => { if (!cancelled) setUnreadApps(Number(data?.applications_unread || 0)); })
                .catch(() => { /* fail soft — no red dot is safer than a wrong one */ });
        };
        refresh();
        const iv = setInterval(refresh, 45_000);
        return () => { cancelled = true; clearInterval(iv); };
    }, [user, location.pathname]);

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <Loader2 size={22} className="animate-spin text-emerald-500" />
            </div>
        );
    }

    const sidebar = [
        {
            section: "Career",
            items: [
                { to: "/professional/dashboard", exact: true, icon: LayoutDashboard, label: "Dashboard", testId: "sidebar-dashboard" },
                { to: "/professional/passport", icon: IdCard, label: "Talent Passport™", testId: "sidebar-passport" },
                // iter 155 — v1 verification simplification. Simonara Verify™
                // per-check marketplace, Employer Screenings view, and
                // Personal Background purchase flow are PARKED for v1 — no
                // user-facing entry point. Routes still exist for admin
                // deeplinks but are no longer in the sidebar. See
                // /app/memory/CHANGELOG.md (Feb 2026 iter-155).
                { to: "/professional/matches", icon: Briefcase, label: "Job Matches", testId: "sidebar-matches" },
                { to: "/professional/job-alerts", icon: BellRing, label: "Job Alerts", testId: "sidebar-job-alerts" },
                { to: "/professional/applications", icon: ListChecks, label: "Application Tracker", testId: "sidebar-applications", badge: unreadApps },
                { to: "/professional/messages", icon: Sparkles, label: "Messages", testId: "sidebar-messages" },
                { to: "/professional/opportunities", icon: Handshake, label: "Opportunities", testId: "sidebar-opportunities" },
                { to: "/professional/badge", icon: BadgeCheck, label: "Verified Badge", testId: "sidebar-badge" },
                // iter 155 — "Background Checks" sidebar entry parked with Verify flows.
            ],
        },
        {
            section: "AI Tools",
            items: [
                { to: "/professional/coach", icon: Compass, label: "Career Coach", testId: "sidebar-coach" },
                { to: "/professional/coach-tools", icon: Sparkles, label: "Coach Tools", testId: "sidebar-coach-tools" },
                { to: "/professional/resume", icon: FileText, label: "Resume Builder", testId: "sidebar-resume" },
                { to: "/professional/interview-coach", icon: Mic, label: "Interview Coach", testId: "sidebar-interview" },
                { to: "/professional/cover-letters", icon: PenLine, label: "Cover Letters", testId: "sidebar-cover-letters" },
                { to: "/professional/salary", icon: DollarSign, label: "Salary Insights", testId: "sidebar-salary" },
            ],
        },
        {
            section: "Account",
            items: [
                { to: "/professional/activity", icon: ListChecks, label: "Activity", testId: "sidebar-activity" },
                { to: "/professional/billing", icon: CreditCard, label: "Billing", testId: "sidebar-billing" },
            ],
        },
    ];

    return (
        <>
            <DashboardLayout sidebar={sidebar} />
            <AgeReattestationGate />
        </>
    );
};

export default TalentLayout;
